import React, {useState, useEffect, useCallback} from 'react';
import Filters from 'components/filters/Filters';
import SortBy from 'components/sortBy/SortBy';
import SearchBar from 'components/searchBar/SearchBar';
import {ROLE_DISPLAY, ROLES} from 'common/constants';
import {FaRegEye} from 'react-icons/fa';
import {Loader, Pagination} from 'components';
import "./_roles.scss";
import {useNavigate} from 'react-router-dom';
import {routes} from 'common';
import { useDispatch, useSelector } from 'react-redux';
import { generalExtraActions } from 'reduxStore/actions';

const filterOptions = [
  {label: ROLE_DISPLAY[ROLES.GROUND_ADMIN], value: ROLES.GROUND_ADMIN},
  {label: ROLE_DISPLAY[ROLES.ADMIN], value: ROLES.ADMIN},
];

const sortOptions = [
  {value: '-created', label: 'Date Added (Newest)'},
  {value: 'created', label: 'Date Added (Oldest)'},
  {value: '-modified', label: 'Date Modified (Newest)'},
  {value: 'modified', label: 'Date Modified (Oldest)'},
];

function Roles() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subRoles, subRolesLoading, subRolesError, subRolesPagination } = useSelector((state) => state.general);

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Reset page to 1 when filter, search, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search, sortBy, itemsPerPage]);

  // Build API parameters
  const buildApiParams = useCallback(() => {
    const params = {
      page: currentPage,
      page_size: itemsPerPage,
    };
    if (sortBy) {
      params.ordering = sortBy;
    }
    if (filter !== 'all') {
      params.role = filter;
    }
    if (search) {
      params.search = search;
    }
    console.log(params)
    return params;
  }, [currentPage, itemsPerPage, sortBy, filter, search]);

  const handleFilter = (status) => {
    setFilter(prev => (prev === status ? 'all' : status));
  };

  const handleSearch = (_search) => {
    setSearch(_search);
  };

  const handleSortChange = (_sortBy) => {
    setSortBy(_sortBy);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (num) => {
    setItemsPerPage(num);
  };

  const handleViewSubRole = (subRoleId) => {
    navigate(`${routes.ROLES}/${subRoleId}`);
  };

  useEffect(() => {
    const params = buildApiParams();
    dispatch(generalExtraActions.getSubRoles(params));
  }, [dispatch, buildApiParams]);

  const totalItems = subRolesPagination.count;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (subRolesError) {
    return (
      <div className="roles-page custom-listing">
        <div className="error-message">
          <h3>Error loading sub roles</h3>
          <p>{subRolesError?.message || 'Something went wrong. Please try again.'}</p>
          <button onClick={() => {
            const params = buildApiParams();
            dispatch(generalExtraActions.getSubRoles(params));
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="roles-page custom-listing">
      {subRolesLoading && <Loader/>}
      <div className="listing-header">
        <h2 className="listing-title">Sub Roles:</h2>
        <button className="listing-create-btn" onClick={() => navigate(routes.ROLE_CREATE)}>Add new sub role</button>
      </div>
      <div className="listing-filters">
        <div className="filter-group">
          <h5>Filters:</h5>
          <Filters filters={filterOptions} selectedFilter={filter} handleFilter={handleFilter}/>
        </div>
        <div className="filter-group">
          <SortBy options={sortOptions} value={sortBy} onChange={handleSortChange}/>
          <SearchBar value={search} onChange={handleSearch} placeholder="Search sub roles..." debounceDelay={500} onDebouncedChange={handleSearch}/>
        </div>
      </div>
      <div className="listing-table-container">
        <table className="listing-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Description</th>
            <th>Role</th>
            <th>Date Added</th>
            <th>Date Modified</th>
            <th>View</th>
          </tr>
          </thead>
          <tbody>
          {subRoles.length === 0 ? (
            <tr>
              <td colSpan="5" style={{textAlign: 'center', color: '#888'}}>
                No sub roles found.
              </td>
            </tr>
          ) : (
            subRoles.map((subRole, idx) => (
              <tr key={subRole.id || idx}>
                <td>{subRole.id}</td>
                <td>{subRole.name}</td>
                <td>{subRole.description?.slice(0, 50) || "-"}</td>
                <td><span className={`badge badge-type`}>{ROLE_DISPLAY[subRole.role] || 'Unknown'}</span></td>
                <td>{subRole.created ? new Date(subRole.created).toLocaleDateString() : 'N/A'}</td>
                <td>{subRole.modified ? new Date(subRole.modified).toLocaleDateString() : 'N/A'}</td>
                <td>
                  <button
                    className="view-btn"
                    onClick={() => handleViewSubRole(subRole.id)}
                    title="View sub role details"
                  >
                    <FaRegEye/>
                  </button>
                </td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </div>
  );
}

export default Roles;